import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getUsageByAccount, getAccountUsageDetail, getAccountProjectEpisodes,
  exportAccountUsageUrl, getToken,
  AccountUsageResponse, AccountUsageRow, AccountUsageDetailItem,
  AccountProjectEpisodesResponse,
} from '../api';
import Breadcrumb from '../components/Breadcrumb';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import { useToast } from '../components/Toast';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8081/api';

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function money(v: number): string {
  return `¥${v.toFixed(2)}`;
}

function formatMonthLabel(m: string): string {
  if (!m) return '-';
  const [y, mo] = m.split('-');
  return `${y} 年 ${parseInt(mo, 10)} 月`;
}

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '0.6rem 0.75rem', fontWeight: 600,
  color: 'var(--text-2)', whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = { padding: '0.6rem 0.75rem', whiteSpace: 'nowrap' };
const subtotalRowStyle: React.CSSProperties = {
  fontWeight: 600, background: 'rgba(99,102,241,0.08)', borderBottom: '1px solid var(--border)',
};

const AccountUsage: React.FC = () => {
  const toast = useToast();
  const [data, setData] = useState<AccountUsageResponse | null>(null);
  const [month, setMonth] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportGroupId, setExportGroupId] = useState(''); // '' = 导出全部
  const [detail, setDetail] = useState<{ userId: string; username: string; items: AccountUsageDetailItem[] } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, AccountProjectEpisodesResponse>>({});
  const [expandLoading, setExpandLoading] = useState<string>('');
  const [copiedProjectId, setCopiedProjectId] = useState('');

  const copyProjectId = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation(); // 行点击是展开/收起,复制不应触发
    if (!navigator.clipboard?.writeText) {
      toast.error('剪贴板不可用');
      return;
    }
    navigator.clipboard.writeText(projectId)
      .then(() => {
        setCopiedProjectId(projectId);
        window.setTimeout(() => setCopiedProjectId(''), 1500);
      })
      .catch(() => toast.error('复制失败'));
  };

  const toggleProject = async (projectId: string) => {
    if (!detail) return;
    if (expanded[projectId]) {
      setExpanded(prev => {
        const next = { ...prev };
        delete next[projectId];
        return next;
      });
      return;
    }
    setExpandLoading(projectId);
    try {
      const res = await getAccountProjectEpisodes(detail.userId, projectId, month);
      setExpanded(prev => ({ ...prev, [projectId]: res }));
    } catch (e: unknown) {
      toast.error(errMsg(e));
    } finally {
      setExpandLoading('');
    }
  };

  const load = useCallback((m?: string) => {
    setLoading(true);
    getUsageByAccount(m, search || undefined)
      .then(res => {
        // Go nil 切片会序列化成 null,统一归一成数组,避免渲染时 .map/.forEach 崩溃
        setData({
          ...res,
          availableMonths: res.availableMonths || [],
          groups: (res.groups || []).map(g => ({ ...g, members: g.members || [] })),
          ungrouped: res.ungrouped || [],
          unknown: res.unknown || { cost: 0 },
        });
        setMonth(res.month);
      })
      .catch(e => toast.error('加载失败: ' + errMsg(e)))
      .finally(() => setLoading(false));
  }, [search, toast]);

  useEffect(() => { load(); }, [load]);

  const goMonth = (delta: number) => {
    if (!month) return;
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    setMonth(next);
    load(next);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const resp = await fetch(`${API_BASE}${exportAccountUsageUrl(month, search || undefined, exportGroupId || undefined)}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        toast.error('导出失败: ' + (body.error || resp.status));
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const gName = exportGroupId ? (data?.groups.find(g => g.groupId === exportGroupId)?.groupName || '') : '';
      a.href = url;
      a.download = `account-usage-${month}${gName ? '-' + gName : ''}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      toast.error('导出失败: ' + errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const openDetail = async (r: AccountUsageRow) => {
    setDetail({ userId: r.userId, username: r.username || r.userId, items: [] });
    setExpanded({});
    setDetailLoading(true);
    try {
      const res = await getAccountUsageDetail(r.userId, month);
      setDetail({ userId: r.userId, username: r.username || r.userId, items: res.items });
    } catch (e: unknown) {
      toast.error('加载明细失败: ' + errMsg(e));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // 顶部合计:组内 + 未分组 + 未知归属
  const totals = (() => {
    const acc = { credits: 0, convertedFee: 0, cost: 0, grossMargin: 0 };
    if (data) {
      const addRow = (r: AccountUsageRow) => {
        acc.credits += r.credits; acc.convertedFee += r.convertedFee;
        acc.cost += r.cost; acc.grossMargin += r.grossMargin;
      };
      data.groups.forEach(g => g.members.forEach(addRow));
      data.ungrouped.forEach(addRow);
      acc.cost += data.unknown.cost;
      acc.grossMargin -= data.unknown.cost;
    }
    return acc;
  })();

  const moneyCell = (v: number): React.CSSProperties => ({
    ...tdStyle, textAlign: 'right', color: v < 0 ? 'var(--danger)' : 'var(--warning)',
  });

  const renderRow = (r: AccountUsageRow, indent: boolean) => (
    <tr key={r.userId} style={{ borderBottom: '1px solid var(--border)' }}>
      <td style={{ ...tdStyle, paddingLeft: indent ? '2rem' : '0.75rem' }}>
        <button
          onClick={() => openDetail(r)}
          className="btn-link"
          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', cursor: 'pointer', fontWeight: 500 }}
        >
          {r.username || r.userId}
        </button>
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>{money(r.convertedFee)}</td>
      <td style={moneyCell(r.cost)}>{money(r.cost)}</td>
      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: r.grossMargin < 0 ? 'var(--danger)' : 'var(--success)' }}>{money(r.grossMargin)}</td>
    </tr>
  );

  const hasRows = !!data && (data.groups.length > 0 || data.ungrouped.length > 0 || data.unknown.cost !== 0);

  return (
    <div className="container">
      <Breadcrumb items={[{ label: '项目', to: '/projects' }, { label: '月度对账' }]} />

      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h1 className="page-title">月度对账</h1>
        <Link to="/admin/account-groups" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">账号组管理</Link>
      </div>

      {/* 月份导航 + 导出 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => goMonth(-1)} disabled={loading}>&lt;</button>
          <span style={{ fontSize: '1.1rem', fontWeight: 600, minWidth: '8rem', textAlign: 'center' }}>{formatMonthLabel(month)}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => goMonth(1)} disabled={loading}>&gt;</button>
        </div>
        {data?.availableMonths && data.availableMonths.length > 0 && (
          <select
            value={month}
            onChange={e => { setMonth(e.target.value); load(e.target.value); }}
            style={{ fontSize: '0.85rem', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text)' }}
          >
            {data.availableMonths.map(m => <option key={m} value={m}>{formatMonthLabel(m)}</option>)}
          </select>
        )}
        <form
          onSubmit={e => { e.preventDefault(); setSearch(searchInput); }}
          style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
        >
          <input
            type="text"
            placeholder="账号名搜索"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', width: '12rem', outline: 'none' }}
          />
          <button type="submit" className="btn btn-primary btn-sm" style={{ fontSize: '0.8rem' }}>搜索</button>
          {search && (
            <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: '0.8rem' }} onClick={() => { setSearchInput(''); setSearch(''); }}>清除</button>
          )}
        </form>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select
            value={exportGroupId}
            onChange={e => setExportGroupId(e.target.value)}
            title="选择导出范围"
            style={{ fontSize: '0.8rem', padding: '0.3rem 0.5rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text)' }}
          >
            <option value="">导出全部</option>
            {(data?.groups || []).map(g => <option key={g.groupId} value={g.groupId}>按组:{g.groupName}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.8rem' }} disabled={exporting || loading} onClick={exportCsv}>
            {exporting ? '导出中…' : '导出 CSV'}
          </button>
        </div>
      </div>

      {/* 合计卡片 */}
      {hasRows && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>折算费用合计</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>{money(totals.convertedFee)}</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>成本合计</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)' }}>{money(totals.cost)}</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>毛利合计</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: totals.grossMargin < 0 ? 'var(--danger)' : 'var(--success)' }}>{money(totals.grossMargin)}</div>
          </div>
        </div>
      )}

      {/* 主表 */}
      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>加载中…</div>
        ) : hasRows ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>账号 / 组</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>折算费用(元)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>成本(元)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>毛利(元)</th>
                </tr>
              </thead>
              <tbody>
                {data!.groups.map(g => (
                  <React.Fragment key={g.groupId}>
                    <tr style={subtotalRowStyle}>
                      <td style={tdStyle}>📁 {g.groupName}<span style={{ color: 'var(--text-3)', fontWeight: 400, marginLeft: '0.4rem' }}>({g.members.length})</span></td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{money(g.subtotal.convertedFee)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{money(g.subtotal.cost)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: g.subtotal.grossMargin < 0 ? 'var(--danger)' : 'var(--success)' }}>{money(g.subtotal.grossMargin)}</td>
                    </tr>
                    {g.members.map(m => renderRow(m, true))}
                  </React.Fragment>
                ))}
                {data!.ungrouped.length > 0 && data!.groups.length > 0 && (
                  <tr><td colSpan={4} style={{ ...tdStyle, color: 'var(--text-3)', fontSize: '0.78rem', paddingTop: '0.9rem' }}>未分组账号</td></tr>
                )}
                {data!.ungrouped.map(r => renderRow(r, false))}
                {data!.unknown.cost !== 0 && (
                  <tr style={{ color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}>
                    <td style={tdStyle}>未知归属<span style={{ marginLeft: '0.4rem', fontSize: '0.75rem' }}>(owner 无法判定)</span></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{money(data!.unknown.cost)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="&#128202;" description={`${formatMonthLabel(month)} 暂无用量记录`} />
        )}
      </div>

      {/* 下钻明细 */}
      <Modal
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? `${detail.username} · ${formatMonthLabel(month)} 项目明细` : ''}
        width="600px"
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-3)' }}>加载中…</div>
        ) : detail && detail.items.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>项目</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>折算费用(元)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>成本(元)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>毛利(元)</th>
                </tr>
              </thead>
              <tbody>
                {detail.items.map(it => {
                  const ex = expanded[it.projectId];
                  return (
                    <React.Fragment key={it.projectId}>
                      <tr
                        style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                        onClick={() => toggleProject(it.projectId)}
                      >
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', paddingLeft: '1rem', fontSize: '0.72rem', color: 'var(--text-3)' }}>
                            <code style={{ fontFamily: 'monospace' }}>{it.projectId}</code>
                            <button
                              type="button"
                              onClick={e => copyProjectId(e, it.projectId)}
                              title={copiedProjectId === it.projectId ? '已复制' : '复制项目ID'}
                              aria-label="复制项目ID"
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                background: 'none', border: 'none', padding: '2px', cursor: 'pointer',
                                color: copiedProjectId === it.projectId ? 'var(--success)' : 'var(--text-3)', lineHeight: 0,
                              }}
                            >
                              {copiedProjectId === it.projectId ? <CheckIcon /> : <CopyIcon />}
                            </button>
                          </div>
                          <div>
                            <span style={{ display: 'inline-block', width: '1rem', color: 'var(--text-3)' }}>
                              {ex ? '▾' : '▸'}
                            </span>
                            {it.projectName || it.projectId}
                            {expandLoading === it.projectId && <span className="loading-spinner" style={{ marginLeft: '0.5rem' }} />}
                          </div>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{money(it.convertedFee)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{money(it.cost)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{money(it.convertedFee - it.cost)}</td>
                      </tr>
                      {ex && [
                        ...ex.episodes,
                        ...(ex.aggregate.credits !== 0 || ex.aggregate.cost !== 0
                          ? [{ ...ex.aggregate, episodeId: '__agg__', episodeName: '综合(不分集)' }]
                          : []),
                      ].map(ep => (
                        <tr key={it.projectId + ':' + ep.episodeId} style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-2)' }}>
                          <td style={{ ...tdStyle, paddingLeft: '2rem', color: 'var(--text-2)' }}>{ep.episodeName}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{money(ep.convertedFee)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{money(ep.cost)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{money(ep.grossMargin)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="&#128230;" description="该账号当月无项目用量" />
        )}
      </Modal>
    </div>
  );
};

export default AccountUsage;
